import { useState, useEffect, useCallback } from 'react';
import { Robot, Sparkle, Code, MagnifyingGlass } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { AutonomousForm, AppBuilderForm, ResearchForm } from '@/components/AgentForms';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AutonomousAgents = () => {
  const [activeMode, setActiveMode] = useState('autonomous');
  const [autonomousGoal, setAutonomousGoal] = useState({ goal: '', description: '', task_type: 'general' });
  const [appBuilder, setAppBuilder] = useState({ app_description: '', features: [], tech_stack: '' });
  const [research, setResearch] = useState({ topic: '', depth: 'medium', include_charts: true });
  const [tasks, setTasks] = useState([]);
  const [processing, setProcessing] = useState(false);

  const loadTasks = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/autonomous/tasks`);
      setTasks(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === "development") console.error("Failed to load autonomous tasks:", error);
    }
  }, []);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  const downloadBlob = (content, filename) => {
    const blob = new Blob([content], { type: 'text/markdown' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const createAutonomousTask = async (e) => {
    e.preventDefault();
    if (!autonomousGoal.goal.trim()) return;
    setProcessing(true);
    try {
      await axios.post(`${API}/autonomous/create`, autonomousGoal);
      toast.success('Autonomous Agent Activated!');
      toast.info('Working in background... Come back in a few minutes!');
      setAutonomousGoal({ goal: '', description: '', task_type: 'general' });
      loadTasks();
    } catch (error) {
      toast.error('Failed to start autonomous agent');
    } finally {
      setProcessing(false);
    }
  };

  const buildApp = async (e) => {
    e.preventDefault();
    if (!appBuilder.app_description.trim()) return;
    setProcessing(true);
    try {
      const response = await axios.post(`${API}/autonomous/app-builder`, appBuilder);
      toast.success('App Specification Generated!');
      downloadBlob(response.data.specification, 'app_specification.md');
      toast.success('Specification downloaded!');
      setAppBuilder({ app_description: '', features: [], tech_stack: '' });
    } catch (error) {
      toast.error('Failed to generate app specification');
    } finally {
      setProcessing(false);
    }
  };

  const doResearch = async (e) => {
    e.preventDefault();
    if (!research.topic.trim()) return;
    setProcessing(true);
    try {
      const response = await axios.post(`${API}/autonomous/research`, research);
      toast.success('Research Completed!');
      downloadBlob(response.data.result, `research_${research.topic.replace(/\s/g, '_')}.md`);
      toast.success('Research report downloaded!');
      setResearch({ topic: '', depth: 'medium', include_charts: true });
    } catch (error) {
      toast.error('Failed to complete research');
    } finally {
      setProcessing(false);
    }
  };

  const modes = [
    { id: 'autonomous', icon: Sparkle, label: 'Autonomous Mode' },
    { id: 'app_builder', icon: Code, label: 'App Builder' },
    { id: 'research', icon: MagnifyingGlass, label: 'Research' },
  ];

  return (
    <div className="h-full flex flex-col bg-black" data-testid="autonomous-agents-panel">
      <div className="border-b border-border p-6 bg-surface">
        <div className="flex items-center gap-3 mb-4">
          <Robot size={32} weight="duotone" className="text-foreground" />
          <div>
            <h2 className="text-2xl font-bold tracking-tight" data-testid="autonomous-header-title">Autonomous Agents</h2>
            <p className="text-sm text-muted-foreground">Set it, forget it, come back to completed work</p>
          </div>
        </div>
        <div className="flex gap-2">
          {modes.map(({ id, icon: Icon, label }) => (
            <Button
              key={id}
              onClick={() => setActiveMode(id)}
              variant={activeMode === id ? 'default' : 'outline'}
              className={activeMode === id ? 'bg-white text-black' : 'border-border'}
              size="sm"
              data-testid={`mode-${id}-button`}
            >
              <Icon size={16} className="mr-2" />
              {label}
            </Button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        {activeMode === 'autonomous' && (
          <AutonomousForm
            autonomousGoal={autonomousGoal}
            setAutonomousGoal={setAutonomousGoal}
            processing={processing}
            onSubmit={createAutonomousTask}
          />
        )}
        {activeMode === 'app_builder' && (
          <AppBuilderForm
            appBuilder={appBuilder}
            setAppBuilder={setAppBuilder}
            processing={processing}
            onSubmit={buildApp}
          />
        )}
        {activeMode === 'research' && (
          <ResearchForm
            research={research}
            setResearch={setResearch}
            processing={processing}
            onSubmit={doResearch}
          />
        )}
      </div>
    </div>
  );
};

export default AutonomousAgents;
